import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { useNotifications } from './NotificationContext';
import { PlacedOrder, OrderItem, DeliveryDriver } from '../types';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';
import { useGeolocation } from '../hooks/useGeolocation';
import { db } from '../firebase/client';
import NotificationService from '../services/NotificationService';
import {
  collection,
  query,
  onSnapshot,
  where,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  deleteField,
  or,
  and,
  limit,
  startAfter,
  getDocs,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { calculateDistance } from '../utils/geoUtils';
import { parseDate } from '../utils/dateUtils';


type OrderStatus = 'En attente' | 'En préparation' | 'Envoyée' | 'Refusée' | 'Annulée' | 'Prête' | 'Terminée' | 'Livrée';

interface AllOrdersContextType {
  placedOrders: PlacedOrder[];
  loading: boolean;
  placeOrder: (items: OrderItem[], totalPrice: number, deliveryFee: number, customerPhone: string, deliveryType: 'delivery' | 'takeaway', location?: { lat: number, lng: number } | null, comment?: string, address?: string) => Promise<PlacedOrder | null>;
  updateOrderStatus: (orderId: string, status: OrderStatus, driver?: DeliveryDriver, preparationTime?: number, livreurStatusOverride?: string) => Promise<void>;
  updateOrderItems: (orderId: string, items: any[], totalPrice: number) => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  nudgeRestaurant: (orderId: string) => Promise<void>;
  respondToNudge: (orderId: string, response: 'coming' | 'delayed', extraMinutes?: number) => Promise<void>;
  loadMoreOrders: () => Promise<void>;
  hasMore: boolean;
  loadingMore: boolean;
}

const AllOrdersContext = createContext<AllOrdersContextType | undefined>(undefined);
const POLLING_INTERVAL = 30000; // Added POLLING_INTERVAL

export const AllOrdersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const { restaurants } = useData();
  const { stopAlarm } = useNotifications();
  const [placedOrders, setPlacedOrders] = useState<PlacedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const activeQueryRef = useRef<any>(null);
  const placedOrdersRef = useRef(placedOrders);
  const lastNotifiedRef = useRef<{ [key: string]: string }>({}); // orderId_status/type -> timestamp
  const announcedOrdersRef = useRef<Set<string>>(new Set()); // orderIds already "alarmed"
  const isInitialLoadRef = useRef(true); // Track initial snapshot

  useEffect(() => {
    placedOrdersRef.current = placedOrders;
  }, [placedOrders]);



  // Helper to normalize location format (GeoPoint vs {lat, lng})
  const normalizeLocation = (loc: any): { lat: number; lng: number } | undefined => {
    if (!loc) return undefined;
    if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
      return { lat: loc.latitude, lng: loc.longitude };
    }
    if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
      return { lat: loc.lat, lng: loc.lng };
    }
    return undefined;
  };

  const mapOrderFromDB = (id: string, o: any): PlacedOrder => ({
    id: id,
    customerName: o.customerName || o.customer_name,
    customerPhone: o.customerPhone || o.customer_phone || 'Non renseigné',
    customerLocation: normalizeLocation(o.customerLocation || o.customer_location),
    customerAddress: o.customerAddress || o.customer_address,
    customerId: o.customerId || o.customer_id,
    restaurantId: o.restaurantId || o.restaurant_id || o.id_restaurant,
    restaurantName: o.restaurantName || o.restaurant_name || (o.items?.[0]?.restaurantName),
    items: o.items,
    totalPrice: Number(o.totalPrice || o.total_price),
    deliveryFee: Number(o.deliveryFee || o.delivery_fee || 0),
    status: o.status as OrderStatus,
    deliveryType: o.deliveryType || o.delivery_type || 'delivery',
    timestamp: parseDate(o.createdAt || o.timestamp || o.preparationAt || o.assignedAt, new Date(), o.orderId) as Date,
    assignedDriver: o.assignedDriver || o.assigned_driver,
    livreurId: o.livreurId || o.livreur_id,
    livreurStatus: o.livreurStatus || o.livreur_status,
    preparationTime: o.preparationTime || o.preparation_time,
    preparationAt: parseDate(o.preparationAt, null as any),
    estimatedDeliveryTime: o.estimatedDeliveryTime || o.estimated_delivery_time,
    comment: o.comment,
    driverRating: o.driverRating || o.driver_rating,
    driverRatingComment: o.driverRatingComment || o.driver_rating_comment,
    restaurantWilaya: o.restaurantWilaya || o.restaurant_wilaya,
    nudgeCount: o.nudgeCount || 0,
    lastNudgeAt: parseDate(o.lastNudgeAt, null as any),
    nudgeResponse: o.nudgeResponse,
    restaurantDriverRating: o.restaurantDriverRating,
    restaurantRating: o.restaurantRating,
    restaurantRatingComment: o.restaurantRatingComment,
    assignedAt: parseDate(o.assignedAt, null as any)
  });

  useEffect(() => {
    if (!currentUser) {
      setPlacedOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    isInitialLoadRef.current = true;
    const unsubs: (() => void)[] = [];
    
    // Safety timeout: stop loading after 8 seconds if no data received
    const loadingTimeout = setTimeout(() => {
        setLoading(false);
    }, 8000);

    const effectiveRestaurantId = currentUser.restaurantId || (currentUser.role === 'restaurateur' ? restaurants.find(r => (r.ownerId === currentUser.uid || (r as any).owner_id === currentUser.uid))?.id : null);

    const listenerResults = new Map<string, Map<string, PlacedOrder>>();

    const handleSnapshot = (listenerId: string, snap: any) => {
      if (!listenerResults.has(listenerId)) {
        listenerResults.set(listenerId, new Map());
      }
      
      const myMap = listenerResults.get(listenerId)!;
      
      snap.docChanges().forEach((change: any) => {
        const orderId = change.doc.id;
        const data = change.doc.data();

        if (change.type === 'removed') {
          myMap.delete(orderId);
          announcedOrdersRef.current.delete(orderId);
        } else {
          const order = mapOrderFromDB(orderId, data);
          myMap.set(orderId, order);

          // LOCAL ALARM TRIGGER LOGIC
          // Only trigger if we aren't loading (initial snapshot) and haven't announced this exact order state recently
          if (currentUser && !isInitialLoadRef.current) {
            const isNew = change.type === 'added' && !announcedOrdersRef.current.has(orderId);
            
            // 1. Restaurateur: New Order or Nudge
            if (currentUser.role === 'restaurateur') {
              const forMe = order.restaurantId === currentUser.restaurantId || (restaurants.some(r => r.id === order.restaurantId && (r.ownerId === currentUser.uid || (r as any).owner_id === currentUser.uid)));
              
              if (forMe) {
                if (isNew && order.status === 'En attente') {
                  console.log(`🔔 [LOCAL_ALARM] New Order detected: ${orderId}`);
                  window.dispatchEvent(new CustomEvent('yumii:notification', { 
                    detail: { 
                      type: 'new_order', 
                      orderId, 
                      restaurantId: order.restaurantId, 
                      role: 'restaurateur',
                      title: "🔔 NOUVELLE COMMANDE",
                      message: `Une nouvelle commande de ${order.customerName} vient d'arriver !`
                    } 
                  }));
                  announcedOrdersRef.current.add(orderId);
                } 
                else if (change.type === 'modified') {
                  // Check for Nudge increase
                  const oldOrder = placedOrdersRef.current.find(o => o.id === orderId);
                  if (order.nudgeCount > (oldOrder?.nudgeCount || 0)) {
                    console.log(`🔔 [LOCAL_ALARM] Nudge detected: ${orderId}`);
                    window.dispatchEvent(new CustomEvent('yumii:notification', { 
                      detail: { 
                        type: 'nudge', 
                        orderId, 
                        restaurantId: order.restaurantId, 
                        role: 'restaurateur',
                        title: "⚠️ RELANCE CLIENT",
                        message: `${order.customerName} demande où en est sa commande !`
                      } 
                    }));
                  }
                }
              }
            }
            
            // 2. Client: Response to Nudge or Status Change
            if (currentUser && currentUser.role !== 'restaurateur' && currentUser.role !== 'livreur') {
              if (change.type === 'modified') {
                const oldOrder = placedOrdersRef.current.find(o => o.id === orderId);
                
                // Detection: Nudge Response
                if (order.nudgeResponse && !oldOrder?.nudgeResponse) {
                  console.log(`🔔 [LOCAL_ALARM] Client Nudge Response detected: ${orderId}`);
                  window.dispatchEvent(new CustomEvent('yumii:notification', { 
                    detail: { 
                      type: 'info', 
                      orderId, 
                      title: "🚀 RÉPONSE DU RESTAURANT",
                      message: order.nudgeResponse === 'coming' 
                        ? "Bonne nouvelle ! Votre commande arrive, ils finissent la préparation." 
                        : `Le restaurant annonce un petit retard de ${order.preparationTime || 10} min.`
                    } 
                  }));
                }

                // Detection: Status Change
                if (order.status !== oldOrder?.status && oldOrder?.status) {
                  console.log(`🔔 [LOCAL_ALARM] Order Status changed: ${orderId} (${order.status})`);
                  const statusMessages: Record<string, string> = {
                    'En préparation': "Le restaurant commence la préparation de votre commande !",
                    'Envoyée': "Votre commande est en route ! 🛵",
                    'Prête': "Votre commande est prête à être récupérée ! 🛍️",
                    'Livrée': "Votre commande a été livrée. Bon appétit ! 🍽️",
                    'Annulée': "Votre commande a été annulée.",
                    'Refusée': "Désolé, le restaurant a refusé votre commande."
                  };

                  if (statusMessages[order.status]) {
                    window.dispatchEvent(new CustomEvent('yumii:notification', { 
                      detail: { 
                        type: 'order_update', 
                        orderId, 
                        title: "📍 SUIVI DE COMMANDE",
                        message: statusMessages[order.status]
                      } 
                    }));
                  }
                }
              }
            }
            
            // 3. Livreur: New Mission Assignment
            if (currentUser.role === 'livreur') {
              if (order.livreurId === currentUser.uid && order.livreurStatus === 'pending') {
                 if (isNew || (change.type === 'modified' && placedOrdersRef.current.find(o => o.id === orderId)?.livreurStatus !== 'pending')) {
                    console.log(`🔔 [LOCAL_ALARM] New Mission detected: ${orderId}`);
                    window.dispatchEvent(new CustomEvent('yumii:notification', { 
                      detail: { 
                        type: 'new_mission', 
                        orderId, 
                        role: 'livreur',
                        title: "🛵 NOUVELLE MISSION",
                        message: "Une nouvelle course est disponible dans votre zone !"
                      } 
                    }));
                    announcedOrdersRef.current.add(orderId);
                 }
              }
            }
          }
        }
      });

      // Combine results from all active listeners
      const globalPool = new Map<string, PlacedOrder>();
      listenerResults.forEach(map => {
        map.forEach((order, id) => {
          globalPool.set(id, order);
        });
      });
      
      setPlacedOrders(processOrders(Array.from(globalPool.values())));
      setLoading(false);
      isInitialLoadRef.current = false;
      clearTimeout(loadingTimeout);
    };

    if (currentUser.role === 'restaurateur') {
        if (!effectiveRestaurantId) {
            setLoading(false);
            clearTimeout(loadingTimeout);
            return;
        }

        // Listener 1: Main ID
        const q1 = query(
            collection(db, 'orders'),
            where('restaurantId', '==', effectiveRestaurantId),
            orderBy('timestamp', 'desc'),
            limit(300)
        );
        unsubs.push(onSnapshot(q1, (snap) => handleSnapshot('q1', snap), err => {
            console.error("❌ Restaurateur Q1 error:", err);
            setLoading(false);
        }));

        // Listener 2: Legacy ID
        const q2 = query(
            collection(db, 'orders'),
            where('id_restaurant', '==', effectiveRestaurantId),
            orderBy('timestamp', 'desc'),
            limit(300)
        );
        unsubs.push(onSnapshot(q2, (snap) => handleSnapshot('q2', snap), err => {
            console.error("❌ Restaurateur Q2 error:", err);
            setLoading(false);
        }));

    } else if (currentUser.role === 'livreur') {
        const myWilaya = currentUser.wilaya || 'NON DÉFINIE';
        
        // Listener 1: Assigned to me
        const qAssigned = query(
            collection(db, 'orders'),
            where('livreurId', '==', currentUser.uid),
            orderBy('timestamp', 'desc'),
            limit(200)
        );
        unsubs.push(onSnapshot(qAssigned, (snap) => handleSnapshot('qAssigned', snap), err => {
            console.error("❌ Livreur Assigned Q error:", err);
            setLoading(false);
        }));

    } else {
        const q = query(
            collection(db, 'orders'),
            where('customerId', '==', currentUser.uid),
            orderBy('timestamp', 'desc'),
            limit(50)
        );
        unsubs.push(onSnapshot(q, (snap) => handleSnapshot('qClient', snap), err => {
            console.error("❌ Client Q error:", err);
            setLoading(false);
        }));
    }

    function processOrders(mappedOrders: PlacedOrder[]) {
      const now = new Date().getTime();
      // Restaurateurs see 30 days of history, others (drivers/clients) see 48h
      const MAX_AGE = 48 * 60 * 60 * 1000; 

      // 1. Deduplicate
      const unique = Array.from(new Map(mappedOrders.map(o => [o.id, o])).values());

      // 2. Filter based on role context
      let filtered = unique.filter(o => {
          const rawTimestamp = (o as any).timestamp;
          const orderTime = parseDate(rawTimestamp).getTime();
          
          // BEEP-FIX: Always show "active" orders regardless of age, up to 7 days
          const isActive = ['En attente', 'En préparation', 'Envoyée', 'Prête'].includes(o.status);
          if (isActive) {
              const isStale = rawTimestamp && (now - orderTime) > (7 * 24 * 3600 * 1000);
              if (!isStale) return true;
          }

          // For completed/cancelled/delivered orders, apply 48h limit
          // If timestamp is null (serverTimestamp pending), it's brand new and should be kept
          const isRecent = !rawTimestamp || (now - orderTime) < MAX_AGE;

          if (currentUser?.role === 'livreur') {
              const isMine = o.livreurId === currentUser.uid;
              
              if (isMine) {
                  if (o.status === 'Terminée' || o.status === 'Livrée' || o.status === 'Annulée') {
                      return (now - orderTime) < (MAX_AGE / 2);
                  }
                  return isRecent;
              }
              return false; // Only show assigned missions
          }

          return isRecent;
      });

      // 3. Sort
      filtered.sort((a, b) => {
          const dateA = parseDate(a.timestamp).getTime();
          const dateB = parseDate(b.timestamp).getTime();
          return dateB - dateA;
      });

      return filtered;
    }

    return () => {
      unsubs.forEach(u => u());
      clearTimeout(loadingTimeout);
    };
  }, [currentUser?.uid, currentUser?.role, currentUser?.wilaya, currentUser?.restaurantId, restaurants]);


  const loadMoreOrders = async () => {
    if (loadingMore || !hasMore || !lastDocRef.current || !activeQueryRef.current) return;

    setLoadingMore(true);
    try {
      const q = query(activeQueryRef.current, startAfter(lastDocRef.current), limit(20));
      const snap = await getDocs(q);

      if (snap.docs.length > 0) {
        const olderOrders = snap.docs.map(d => mapOrderFromDB(d.id, d.data()));
        setPlacedOrders(prev => {
          // Filter out duplicates in case onSnapshot updated during fetch
          const existingIds = new Set(prev.map(o => o.id));
          const uniqueOlder = olderOrders.filter(o => !existingIds.has(o.id));
          return [...prev, ...uniqueOlder];
        });
        lastDocRef.current = snap.docs[snap.docs.length - 1];
        setHasMore(snap.docs.length === 20);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading more orders:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Global Driver Tracking Logic
  const { startTracking, stopTracking, error: geoError, errorCode, lastSyncTimestamp, isOnline: geoIsOnline } = useGeolocation(currentUser?.uid);
  const lastTrackedOrderId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // ONLY force offline if it's a permission error (Code 1)
    // Code 2 (Unavailable) and 3 (Timeout) are transient and shouldn't disconnect the driver
    if (errorCode === 1 && currentUser?.role === 'livreur' && currentUser?.isOnline) {
      console.warn('[TRACKING] Geolocation PERMISSION error. Forcing driver offline:', geoError);
      const userRef = doc(db, 'users', currentUser.uid);
      updateDoc(userRef, { isOnline: false }).catch(console.error);
    } else if (geoError && currentUser?.role === 'livreur' && currentUser?.isOnline) {
      console.log('[TRACKING] Transient Geolocation error (signal weak/timeout). Driver stays online but tracking might be delayed.');
    }
  }, [geoError, errorCode, currentUser]);

  // Active Order ID for tracking dependency
  const activeOrderId = React.useMemo(() => {
    if (currentUser?.role !== 'livreur') return null;
    const active = placedOrders.find(o =>
      o.livreurId === currentUser.uid &&
      o.livreurStatus === 'accepted' &&
      ['En attente', 'En préparation', 'Prête', 'Envoyée'].includes(o.status)
    );
    return active?.id || null;
  }, [placedOrders, currentUser?.role, currentUser?.uid]);

  useEffect(() => {
    // If user is a livreur and online
    if (currentUser?.role === 'livreur' && currentUser?.isOnline && !geoError) {
      if (lastTrackedOrderId.current !== activeOrderId) {
        if (activeOrderId) {
          console.log(`[TRACKING] Active order CHANGED (${activeOrderId}), starting RTDB tracking...`);
          startTracking(activeOrderId);
        } else {
          console.log(`[TRACKING] No active mission. Basic Firestore tracking only.`);
          startTracking();
        }
        lastTrackedOrderId.current = activeOrderId;
      }
    } else {
      if (lastTrackedOrderId.current !== undefined) {
        stopTracking();
        lastTrackedOrderId.current = undefined;
      }
    }
  }, [currentUser?.role, currentUser?.isOnline, activeOrderId, startTracking, stopTracking, currentUser?.uid, geoError]);

  // Periodic Keep-alive / Heartbeat for Geolocation
  useEffect(() => {
    if (currentUser?.role === 'livreur' && currentUser?.isOnline) {
      const heartbeat = setInterval(() => {
        const now = Date.now();
        const lastSync = lastSyncTimestamp || 0;
        const timeSinceSync = now - lastSync;

        // If no sync for more than 3 minutes, force a restart or manual sync
        if (timeSinceSync > 180000) {
            console.log(`[TRACKING] Heartbeat ALERT: No sync for ${Math.round(timeSinceSync/1000)}s. Forcing restart...`);
            const activeOrder = placedOrdersRef.current.find(o =>
                o.livreurId === currentUser.uid &&
                (o.status === 'Envoyée' || (o.status === 'En préparation' && o.livreurStatus === 'accepted'))
            );
            startTracking(activeOrder?.id);
        } else {
            console.log(`[TRACKING] Heartbeat OK: Last sync ${Math.round(timeSinceSync/1000)}s ago.`);
        }
      }, 60000); // Check every minute

      return () => clearInterval(heartbeat);
    }
  }, [currentUser?.role, currentUser?.isOnline, currentUser?.uid, startTracking, lastSyncTimestamp]);


  const placeOrder = async (items: OrderItem[], totalPrice: number, deliveryFee: number, customerPhone: string, deliveryType: 'delivery' | 'takeaway', location?: { lat: number, lng: number } | null, comment?: string, address?: string): Promise<PlacedOrder | null> => {
    if (!currentUser || items.length === 0) return null;
    const restaurantId = items[0].restaurantId;
    const restaurant = restaurants.find(r => r.id === restaurantId);

    // Final Safety Check: Force takeaway if restaurant has disabled delivery
    let finalDeliveryType = deliveryType;
    let finalDeliveryFee = deliveryFee;

    if (restaurant && restaurant.deliveryEnabled === false) {
      console.log(`⚠️ [Order] Forcing takeaway for restaurant ${restaurant.name} (Delivery Disabled)`);
      finalDeliveryType = 'takeaway';
      finalDeliveryFee = 0;
    }

    // Use provided location or fallback to user profile location
    const finalLocation = location || currentUser.location || null;

    const orderId = `ord_${Math.random().toString(36).substr(2, 4).toUpperCase()}_${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '')}`;

    const newOrderData: any = {
      orderId: orderId, // Custom human-readable ID
      customerId: currentUser.uid,
      customerName: currentUser.name,
      customerPhone: customerPhone,
      customerLocation: finalLocation,
      customerAddress: address || (currentUser as any)?.address || '',
      restaurantId: restaurantId,
      restaurantName: items[0].restaurantName,
      restaurantWilaya: restaurants.find(r => r.id === restaurantId)?.wilaya || '',
      items: items,
      totalPrice: totalPrice,
      deliveryFee: finalDeliveryFee,
      status: 'En attente',
      deliveryType: finalDeliveryType,
      livreurId: null, // Explicitly set to null for Firestore query compatibility
      livreurStatus: 'pending',
    };

    if (comment && comment.trim() !== '') {
      newOrderData.comment = comment.trim();
    }

    try {
      const cleanedData = cleanObject(newOrderData);
      
      // Assign timestamps AFTER cleaning to avoid accidental mangling of sentinels
      cleanedData.createdAt = serverTimestamp();
      cleanedData.timestamp = serverTimestamp();

      const docRef = await addDoc(collection(db, 'orders'), cleanedData);

      // Trigger Push Notification to Restaurant
      if (restaurant) {
        const ownerId = restaurant.ownerId || (restaurant as any).owner_id;
        if (ownerId) {
          NotificationService.notifyRestaurant(
            ownerId, 
            "🔔 NOUVELLE COMMANDE", 
            `Vous avez une nouvelle commande de ${currentUser.name} (${totalPrice} DA)`,
            { orderId: docRef.id, type: 'new_order' }
          ).catch(err => console.error("Push notification failed:", err));
        }
      }

      return mapOrderFromDB(docRef.id, { ...newOrderData, createdAt: new Date() });
    } catch (error) {
      console.error("Error creating order:", error);
      return null;
    }
  };

  // Helper to remove undefined values recursively (Firestore doesn't allow undefined)
  // Optimization: Skip recursion for complex Firebase objects (detected by known properties)
  const cleanObject = (obj: any): any => {
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) return obj;
    
    // Detection compatible with minification (don't use constructor.name)
    const isTimestamp = (typeof obj.seconds === 'number' && typeof obj.nanoseconds === 'number');
    const isGeoPoint = (typeof obj.latitude === 'number' && typeof obj.longitude === 'number');
    // FieldValue detection: checking for common 'sentinel' or internal markers 
    // to avoid mangling serverTimestamp()
    const isSentinel = (obj && (
      obj._methodName === 'serverTimestamp' || 
      obj._methodName === 'arrayUnion' || 
      obj._methodName === 'arrayRemove' || 
      obj._methodName === 'increment' ||
      obj.type === 'serverTimestamp' || 
      (typeof obj.isEqual === 'function' && !isTimestamp && !isGeoPoint)
    ));
    
    if (isTimestamp || isGeoPoint || isSentinel) {
      return obj;
    }

    const newObj: any = Array.isArray(obj) ? [] : {};
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (val !== undefined) {
        newObj[key] = (typeof val === 'object' && val !== null && !(val instanceof Date))
          ? cleanObject(val)
          : val;
      }
    });
    return newObj;
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus, driver?: DeliveryDriver, preparationTime?: number, livreurStatusOverride?: string) => {
    // Capture state BEFORE update to avoid race condition with onSnapshot and status comparison
    const currentOrder = placedOrdersRef.current.find(o => o.id === orderId);

    try {
      let updateData: any = cleanObject({ status, preparationTime });

      // Set preparationAt timestamp when order is accepted/starts preparation
      if (status === 'En préparation' && currentOrder?.status !== 'En préparation') {
        updateData.preparationAt = serverTimestamp();
      }

      if (driver) {
        updateData.assignedDriver = cleanObject(driver);
        updateData.livreurId = driver.id;

        // Normalize ids for comparison
        const currentDriverId = driver.id;
        const existingDriverId = currentOrder?.livreurId || currentOrder?.assignedDriver?.id;
        const existingStatus = currentOrder?.livreurStatus;
        
        const isSameDriver = existingDriverId === currentDriverId;

        if (livreurStatusOverride === 'rejected') {
          // BEEP-FIX: Automatically clear attribution details on rejection
          updateData.livreurId = null;
          updateData.assignedDriver = null;
          updateData.livreurStatus = null;
          updateData.assignedAt = null;
        } else if (livreurStatusOverride) {
          updateData.livreurStatus = livreurStatusOverride;
        } else if (driver.isManual || (isSameDriver && existingStatus === 'accepted')) {
          updateData.livreurStatus = 'accepted';
        } else if (!existingDriverId || !isSameDriver) {
          updateData.livreurStatus = 'pending'; // Manual assignment by restaurant or system
          updateData.assignedAt = serverTimestamp(); // BEEP-FIX: Use server timestamp for consistency
        } else if (existingStatus) {
          updateData.livreurStatus = existingStatus; // Preserve existing acceptance or other status
        }

        // Calculate estimated delivery time if driver is accepted
        if (updateData.livreurStatus === 'accepted' || (existingStatus === 'accepted' && status === 'Envoyée')) {
          const restaurant = restaurants.find(r => r.id === currentOrder?.restaurantId);
          if (restaurant?.location?.lat && restaurant?.location?.lng && currentOrder?.customerLocation?.lat && currentOrder?.customerLocation?.lng) {
            let totalDist = 0;
            // Add distance to restaurant only if driver location is known
            // Normalisation des propriétés lat/lng vs latitude/longitude (GeoPoint)
            const driverLoc = driver.location as any;
            const driverLat = driverLoc?.latitude || driverLoc?.lat;
            const driverLng = driverLoc?.longitude || driverLoc?.lng;

            if (driverLat && driverLng) {
              totalDist += calculateDistance(driverLat, driverLng, restaurant.location.lat, restaurant.location.lng);
            }
            
            // Add delivery distance (Resto to Client)
            totalDist += calculateDistance(restaurant.location.lat, restaurant.location.lng, currentOrder.customerLocation.lat, currentOrder.customerLocation.lng);
            
            // Assume 20km/h avg speed (3 min per km) + 10 min buffer
            const estimatedMinutes = Math.round((totalDist * 3) + 10);
            // Round to nearest 5 minutes
            updateData.estimatedDeliveryTime = Math.max(10, Math.ceil(estimatedMinutes / 5) * 5);
          }
        }
      }

      // If completing the order or marking as delivered by driver, ensure livreurStatus reflects this
      if (status === 'Terminée' || status === 'Livrée') {
        updateData.livreurStatus = 'completed';
      }

      console.log(`[DEBUG] Executing updateDoc for order ${orderId} with data:`, updateData);
      if (livreurStatusOverride) {
          console.log(`[DEBUG] Applying livreurStatusOverride: ${livreurStatusOverride}`);
      }
      await updateDoc(doc(db, 'orders', orderId), updateData);
      console.log(`[DEBUG] updateDoc success for order ${orderId}`);
      
      // STOP ALARM when user interacts with the order
      stopAlarm();
    } catch (error) {
      console.error("Error updating order status:", error);
      throw error;
    }
  };

  const updateOrderItems = async (orderId: string, items: any[], totalPrice: number) => {
    try {
      if (!currentUser) throw new Error("Not authenticated");
      const cleanItems = items.map(item => cleanObject(item));
      await updateDoc(doc(db, 'orders', orderId), {
          items: cleanItems,
          totalPrice
      });
    } catch (err) {
      console.error("Error updating order items:", err);
      throw err;
    }
  };

  const cancelOrder = async (orderId: string) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { status: 'Annulée' });
    } catch (error) {
      console.error("Error cancelling order:", error);
    }
  };

  const nudgeRestaurant = async (orderId: string) => {
    const currentOrder = placedOrdersRef.current.find(o => o.id === orderId);
    if (!currentOrder) return;

    try {
      const restaurant = restaurants.find(r => r.id === currentOrder.restaurantId);
      const ownerId = restaurant?.ownerId || (restaurant as any)?.owner_id;

      await updateDoc(doc(db, 'orders', orderId), {
        nudgeCount: (currentOrder.nudgeCount || 0) + 1,
        lastNudgeAt: serverTimestamp(),
        nudgeResponse: deleteField() // Reset response on new nudge
      });

      // Trigger Push Notification to Restaurant Owner
      if (ownerId) {
        NotificationService.notifyRestaurant(
          ownerId, 
          "⚠️ RELANCE CLIENT", 
          `${currentOrder.customerName} demande où en est sa commande !`,
          { orderId, type: 'nudge' }
        ).catch(err => console.error("Push nudge failed:", err));
      }

      console.log(`Relance envoyée pour la commande ${orderId}`);
    } catch (err) {
      console.error("Error nudging restaurant:", err);
    }
  };

  const respondToNudge = async (orderId: string, response: 'coming' | 'delayed', extraMinutes?: number) => {
    const currentOrder = placedOrdersRef.current.find(o => o.id === orderId);
    if (!currentOrder) return;

    try {
      const updateData: any = { 
        nudgeResponse: response,
        nudgeDelay: extraMinutes || 10 
      };
      
      // Optimization: Update local state immediately
      setPlacedOrders(prev => prev.map(o => o.id === orderId ? { ...o, nudgeResponse: response, nudgeDelay: extraMinutes || 10 } : o));

      if (extraMinutes && extraMinutes > 0) {
        updateData.preparationTime = extraMinutes;
        updateData.preparationAt = serverTimestamp(); // Reset timer to count down from extraMinutes
      }

      await updateDoc(doc(db, 'orders', orderId), updateData);
      
      // Trigger Push Notification to Client
      if (currentOrder.customerId) {
        NotificationService.notifyClient(
          currentOrder.customerId,
          response === 'coming' ? "🚀 Votre commande arrive !" : "⏳ Petit retard annoncé",
          response === 'coming' 
            ? "Le restaurant finit votre commande, elle sera prête dans un instant." 
            : `Le restaurant a besoin de ${extraMinutes || 10} minutes supplémentaires.`,
          { orderId, type: 'nudge_response' }
        ).catch(err => console.error("Push response failed:", err));
      }

      console.log(`Réponse au nudge envoyée: ${response}`);
    } catch (err) {
      console.error("Error responding to nudge:", err);
    }
  };

  return (
    <AllOrdersContext.Provider value={{ 
      placedOrders, 
      loading, 
      placeOrder, 
      updateOrderStatus, 
      updateOrderItems, 
      cancelOrder, 
      nudgeRestaurant, 
      respondToNudge,
      loadMoreOrders,
      hasMore,
      loadingMore
    }}>
      {children}
    </AllOrdersContext.Provider>
  );
};

export const useAllOrders = () => {
  const context = useContext(AllOrdersContext);
  if (context === undefined) throw new Error('useAllOrders must be used within a AllOrdersProvider');
  return context;
};